// frontend/src/components/admin/SubscriberManagement.js
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './SubscriberManagement.css';

const SubscriberManagement = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [pagination, setPagination] = useState({
    skip: 0,
    limit: 20,
    total: 0
  });

  // Edit form
  const [editForm, setEditForm] = useState({
    email: '',
    dogecoin_wallet: '',
    status: 'active',
    country: ''
  });

  // Create form
  const [createForm, setCreateForm] = useState({
    email: '',
    dogecoin_wallet: '',
    status: 'active'
  });

  useEffect(() => {
    fetchSubscribers();
  }, [pagination.skip, pagination.limit, filterStatus]);

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const params = {
        skip: pagination.skip,
        limit: pagination.limit
      };
      if (filterStatus) {
        params.status = filterStatus;
      }

      const response = await api.get(
        '/admin/subscribers',
        {
          params,
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSubscribers(response.data.subscribers);
      setPagination(prev => ({ ...prev, total: response.data.total }));
      setError('');
    } catch (err) {
      console.error('Failed to fetch subscribers:', err);
      setError('Failed to load subscribers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubscriber = (subscriber) => {
    setSelectedSubscriber(subscriber);
    setEditForm({
      email: subscriber.email,
      dogecoin_wallet: subscriber.dogecoin_wallet || '',
      status: subscriber.status,
      country: subscriber.country || ''
    });
    setShowEditModal(true);
  };

  const saveSubscriberEdit = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      await api.put(
        `/admin/subscribers/${selectedSubscriber.id}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowEditModal(false);
      fetchSubscribers();
      alert('Subscriber updated successfully');
    } catch (err) {
      console.error('Failed to update subscriber:', err);
      alert(err.response?.data?.detail || 'Failed to update subscriber');
    }
  };

  const handleCreateSubscriber = () => {
    setCreateForm({
      email: '',
      dogecoin_wallet: '',
      status: 'active'
    });
    setShowCreateModal(true);
  };

  const saveCreateSubscriber = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      await api.post(
        '/admin/subscribers',
        createForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowCreateModal(false);
      fetchSubscribers();
      alert('Subscriber created successfully');
    } catch (err) {
      console.error('Failed to create subscriber:', err);
      alert(err.response?.data?.detail || 'Failed to create subscriber');
    }
  };

  const handleDeleteSubscriber = async (subscriber) => {
    if (!window.confirm(`Delete subscriber ${subscriber.email}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      await api.delete(
        `/admin/subscribers/${subscriber.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      fetchSubscribers();
      alert('Subscriber deleted successfully');
    } catch (err) {
      console.error('Failed to delete subscriber:', err);
      alert('Failed to delete subscriber');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'active': 'badge-active',
      'confirmed': 'badge-confirmed',
      'pending': 'badge-pending',
      'unsubscribed': 'badge-unsubscribed'
    };
    return statusMap[status] || 'badge-default';
  };

  if (loading && subscribers.length === 0) {
    return <div className="loading">Loading subscribers...</div>;
  }

  return (
    <div className="subscriber-management">
      <div className="subscriber-management-header">
        <h2>👥 Subscriber Management</h2>
        <div className="header-actions">
          <button className="btn-create" onClick={handleCreateSubscriber}>
            ➕ Add Subscriber
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filters">
        <div className="filter-group">
          <label>Filter by Status:</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="confirmed">Confirmed</option>
            <option value="pending">Pending</option>
            <option value="unsubscribed">Unsubscribed</option>
          </select>
        </div>
        <div className="stats-inline">
          <span>Total: {pagination.total}</span>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Table */}
      <div className="subscribers-table-container">
        <table className="subscribers-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Status</th>
              <th>Dogecoin Wallet</th>
              <th>Country</th>
              <th>Source</th>
              <th>Subscribed</th>
              <th>Confirmed</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {subscribers.map((subscriber) => (
              <tr key={subscriber.id}>
                <td className="email-cell">{subscriber.email}</td>
                <td>
                  <span className={`badge ${getStatusBadgeClass(subscriber.status)}`}>
                    {subscriber.status}
                  </span>
                </td>
                <td>
                  {subscriber.dogecoin_wallet ? (
                    <span className="wallet-address" title={subscriber.dogecoin_wallet}>
                      {subscriber.dogecoin_wallet.substring(0, 8)}...
                    </span>
                  ) : (
                    <span className="no-wallet">None</span>
                  )}
                </td>
                <td>{subscriber.country || 'N/A'}</td>
                <td>
                  <span className="source-badge">
                    {subscriber.source || 'website'}
                  </span>
                </td>
                <td>{formatDate(subscriber.subscribed_at)}</td>
                <td>{formatDate(subscriber.confirmed_at)}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn-edit"
                      onClick={() => handleEditSubscriber(subscriber)}
                      title="Edit subscriber"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-delete"
                      onClick={() => handleDeleteSubscriber(subscriber)}
                      title="Delete subscriber"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="pagination">
        <button
          disabled={pagination.skip === 0}
          onClick={() => setPagination(prev => ({
            ...prev,
            skip: Math.max(0, prev.skip - prev.limit)
          }))}
        >
          Previous
        </button>
        <span>
          Showing {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
        </span>
        <button
          disabled={pagination.skip + pagination.limit >= pagination.total}
          onClick={() => setPagination(prev => ({
            ...prev,
            skip: prev.skip + prev.limit
          }))}
        >
          Next
        </button>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Subscriber</h3>
            
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Dogecoin Wallet</label>
              <input
                type="text"
                value={editForm.dogecoin_wallet}
                onChange={(e) => setEditForm({ ...editForm, dogecoin_wallet: e.target.value })}
                placeholder="Optional"
              />
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                value={editForm.status}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="confirmed">Confirmed</option>
                <option value="pending">Pending</option>
                <option value="unsubscribed">Unsubscribed</option>
              </select>
            </div>

            <div className="form-group">
              <label>Country</label>
              <input
                type="text"
                value={editForm.country}
                onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                placeholder="Optional"
              />
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowEditModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={saveSubscriberEdit}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Subscriber</h3>
            
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Dogecoin Wallet</label>
              <input
                type="text"
                value={createForm.dogecoin_wallet}
                onChange={(e) => setCreateForm({ ...createForm, dogecoin_wallet: e.target.value })}
                placeholder="Optional"
              />
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                value={createForm.status}
                onChange={(e) => setCreateForm({ ...createForm, status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="confirmed">Confirmed</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={saveCreateSubscriber}>
                Create Subscriber
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriberManagement;